//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { ReactElement, useEffect, useState } from 'react'

import { Box, Button, Divider, Grid, Snackbar, TextField, Typography } from '@mui/material'

import { SubmitHandler, useForm } from 'react-hook-form'

import EditField from '@react/components/elements/EditField'
import { onPromise } from '@react/components/UiHelpers'
import { useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'
import { ERC20Wei, erc20WeiToCommaDecString } from '@shared/conversions'
import { PoolTypeEnum, yupErc20Wei, yupEthAddr } from '@shared/Enums'

import { ETH_ADDR_ZERO, EthAddr, StringTypeEnum, toFieldMetaData } from '@shared/SchemaTypes'
import { wsServerActionClockRefresh } from '@shared/WsServerActions'
import { Contract, Signer, TransactionReceipt, ZeroAddress, ethers } from 'ethers'
import * as yup from 'yup'
import { InferType } from 'yup'

import { yupResolver } from '@hookform/resolvers/yup'
import { EthersError } from '@shared/AlertCTO'

import {
  PoolBaseOverviewCTO,
  PoolDynamicOverviewCTO,
  PoolEventCTO,
  PoolFlexOverviewCTO,
  PoolOverviewCTO,
} from '@shared/sharedTypes'

import { Erc20Abi, MockERC20Abi } from '@shared/Abis'
import { advanceTimeInHours } from '@shared/localTimeManagement'
import { useNavigate } from 'react-router-dom'

import { refreshPoolCTOThunk } from '@react/chainThunks/refreshPoolCTOThunk'
import { fetchErc20BalanceForPool, fetchPoolTermEarlyWithdrawEventCTOs } from '@react/fetchers/PoolFetchers'
import { logOT } from '@shared/loggingShared'

const transferLiquidityAsset = async (
  liquidityAssetAddr: EthAddr,
  addr: EthAddr,
  amount: ERC20Wei,
  signer: ethers.Signer
): Promise<number> => {
  const transferAmount = BigInt(amount).toString()

  // eslint-disable-next-line @typescript-eslint/no-var-requires

  const usdc = new Contract(liquidityAssetAddr, MockERC20Abi, signer)

  const startingBalance = await usdc.balanceOf(addr)
  //  const sourceBalance = await usdc.balanceOf(signer.getAddress())

  const txMint = await usdc.transfer(addr, transferAmount)
  await txMint.wait(0)
  const endingBalance = await usdc.balanceOf(addr)

  logOT.debug(
    `Transfer ${amount}USDC to ${addr} starting balance ${Number(startingBalance) / 1e6}  ending ${
      Number(endingBalance) / 1e6
    }`
  )
  return Number(endingBalance) / 1e6
}
function AddUsdcForm(): ReactElement {
  const {
    userConnection: { signer },
    chainConfiguration: { perimeterAddresses },
  } = useOtChainState()

  const AddUsdcFormSchema = yup.object({
    addr: yupEthAddr
      .label('addr')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'addr',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    amount: yupErc20Wei
      .label('amount')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'amount',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
  })

  interface AddUsdcFormDTO extends InferType<typeof AddUsdcFormSchema> {
    addr: EthAddr
    amount: ERC20Wei
  }

  const initialValues = {
    addr: ETH_ADDR_ZERO,
    amount: '0' as ERC20Wei,
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddUsdcFormDTO>({
    defaultValues: initialValues,
    resolver: yupResolver(AddUsdcFormSchema),
  })

  const onSubmit: SubmitHandler<AddUsdcFormDTO> = (addUsdcFormDTO: AddUsdcFormDTO): void => {
    logOT.debug('addUsdcFormDTO onSubmit', addUsdcFormDTO)
    if (signer && perimeterAddresses) {
      transferLiquidityAsset(
        perimeterAddresses.liquidityAssets.find((sc) => {
          sc.symbol == 'USDC' || sc.symbol == 'MUSDC'
        })?.addr ?? ZeroAddress,
        addUsdcFormDTO.addr,
        addUsdcFormDTO.amount,
        signer
      )
        .then((balance) => {
          logOT.debug('addUSDC balance', balance)
        })
        .catch((e) => {
          logOT.error('addUSDC error', e)
        })
    }
  }

  return (
    <>
      <Typography variant="bodyBold" gutterBottom component="div">
        Transfer USDC to address
      </Typography>
      <form onSubmit={onPromise(handleSubmit(onSubmit))}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={AddUsdcFormSchema.describe().fields.addr}
            ></EditField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={AddUsdcFormSchema.describe().fields.amount}
            ></EditField>
          </Grid>

          <Grid item xs={12} sm={12}>
            {
              <Button type="submit" data-testid={'submit'} variant="contained">
                Submit
              </Button>
            }
          </Grid>
        </Grid>
      </form>
    </>
  )
}

export default function DemoManagement(): ReactElement {
  const [transactionHash, setTransactionHash] = React.useState<string>('')
  const [transactionOutput, setTransactionOutput] = React.useState<object>({})

  const [performingAction, setPerformingAction] = React.useState<boolean>(false)
  const [actionString, setActionString] = React.useState<string>('')
  const [numberOfDays, setNumberOfDays] = React.useState<number>(2)
  const [numberOfHours, setNumberOfHours] = React.useState<number>(24)

  const {
    webSocket,
    chainConfiguration: { chainConfigurationName, providerReadOnly },
  } = useOtChainState()

  const providerJsonRpc = providerReadOnly as ethers.JsonRpcProvider

  const poolOverviewCTOs = useOtSelector((state) => state.poolState.poolOverviewCTOs)

  const transferBlocks = poolOverviewCTOs
    ? Object.values(poolOverviewCTOs).map((poolOverviewCTO, index) => {
        return <DemoBorrowerVaultTransfer poolAddr={poolOverviewCTO.poolAddr} key={index} />
      })
    : []

  function DemoBorrowerVaultTransfer(props: { poolAddr: EthAddr }): ReactElement {
    const { poolAddr } = props

    const {
      stageConfiguration: { restAPIKey, serverApiBaseUrl },
      userConnection: { signer },
      chainConfiguration: { providerReadOnly },
    } = useOtChainState()

    const [Erc20TransferAmount, setErc20TransferAmount] = React.useState<number>(0)
    const poolBaseOverviewCTO = useOtSelector(
      (state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolBaseOverviewCTO
    )
    const poolOverviewCTO =
      poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term ? (poolBaseOverviewCTO as PoolOverviewCTO) : null
    const poolFlexOverviewCTO =
      poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate ? (poolBaseOverviewCTO as PoolFlexOverviewCTO) : null
    const poolDynamicOverviewCTO =
      poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseOverviewCTO as PoolDynamicOverviewCTO) : null

    const [borrowerWalletBalance, setBorrowerWalletBalance] = React.useState<bigint>(0n)
    const [borrowerVaultBalance, setBorrowerVaultBalance] = React.useState<bigint>(0n)

    const [withdrawEarlyEventCTOs, setWithdrawEarlyEventCTOs] = useState<PoolEventCTO[]>([])

    const loanAddr = poolOverviewCTO?.activeLoanState.loanAddr
    const navigate = useNavigate()

    const dispatch = useOtDispatch()
    const earlyWithdrawApprovedPrincipal = withdrawEarlyEventCTOs
      .filter((eventCTO) => eventCTO.eventType === 'WithdrawEarlyAccepted')
      .map((eventCTO: PoolEventCTO) => eventCTO.principal)
      .reduce(
        (accumulator: number, principal: string | undefined) => accumulator + (principal ? Number(principal) : 0),
        0
      )

    useEffect(() => {
      if (poolFlexOverviewCTO) {
        setErc20TransferAmount(Number(BigInt(poolFlexOverviewCTO.totalRequestedAssets)) - Number(borrowerVaultBalance))
      }
      if (poolOverviewCTO) {
        setErc20TransferAmount(
          Number(poolOverviewCTO?.rolloverNetRepaymentWithFees) -
            Number(borrowerVaultBalance) +
            earlyWithdrawApprovedPrincipal
        )
      }
      if (poolDynamicOverviewCTO) {
        setErc20TransferAmount(123123)
      }
    }, [
      poolBaseOverviewCTO,
      poolFlexOverviewCTO,
      poolDynamicOverviewCTO,
      poolOverviewCTO,
      borrowerVaultBalance,
      withdrawEarlyEventCTOs,
    ])

    useEffect(() => {
      async function fetchEarlyWithdraws(): Promise<void> {
        if (providerReadOnly && serverApiBaseUrl && restAPIKey && signer && loanAddr && loanAddr !== ETH_ADDR_ZERO) {
          const requests = await fetchPoolTermEarlyWithdrawEventCTOs(
            serverApiBaseUrl,
            restAPIKey,
            poolAddr,
            loanAddr,
            null
          )
          setWithdrawEarlyEventCTOs(requests)
        }
      }

      void fetchEarlyWithdraws()
    }, [poolBaseOverviewCTO, loanAddr])

    const handleUsdcTransferToPoolFromLender = () => {
      const asyncFunc = async (): Promise<void> => {
        if (poolDynamicOverviewCTO) {
          const signerKey = process.env.REACT_APP_ACCOUNT_LENDER1 as string

          const lenderSigner = new ethers.Wallet(signerKey, providerReadOnly)

          console.log('poolDynamicOverviewCTO.liquidityAssetAddr', poolDynamicOverviewCTO.liquidityAssetAddr, !!signer)

          const mockUSDCSigned = new ethers.Contract(poolDynamicOverviewCTO.liquidityAssetAddr, Erc20Abi, lenderSigner)

          const amountInWei = Math.round(Erc20TransferAmount * 1e6).toString() as ERC20Wei

          await (await mockUSDCSigned.approve(poolAddr, amountInWei)).wait()
          const tx = await mockUSDCSigned.transfer(poolAddr, amountInWei)
          await tx.wait()
        }
      }
      asyncFunc().catch((e) => {
        console.log(e)
      })
    }
    const handleUsdcTransferToPoolFromBorrower = () => {
      const asyncFunc = async (): Promise<void> => {
        if (poolDynamicOverviewCTO) {
          const signerKey = process.env.REACT_APP_ACCOUNT_BORROWER as string

          const lenderSigner = new ethers.Wallet(signerKey, providerReadOnly)

          console.log('poolDynamicOverviewCTO.liquidityAssetAddr', poolDynamicOverviewCTO.liquidityAssetAddr, !!signer)

          const mockUSDCSigned = new ethers.Contract(poolDynamicOverviewCTO.liquidityAssetAddr, Erc20Abi, lenderSigner)

          const amountInWei = Math.round(Erc20TransferAmount * 1e6).toString() as ERC20Wei

          await (await mockUSDCSigned.approve(poolAddr, amountInWei)).wait()
          const tx = await mockUSDCSigned.transfer(poolAddr, amountInWei)
          await tx.wait()
        }
      }
      asyncFunc().catch((e) => {
        console.log(e)
      })
    }
    const handleUsdcTransfer = (): void => {
      if (poolBaseOverviewCTO) {
        const asyncFunction = async (): Promise<void> => {
          setPerformingAction(true)
          setActionString(
            `Transferring ${poolBaseOverviewCTO.liquidityTokenSymbol} ${Erc20TransferAmount} to ${
              poolDynamicOverviewCTO ? poolDynamicOverviewCTO.poolAddr : poolBaseOverviewCTO.borrowerVaultAddr
            }`
          )

          const erc20Contract = new Contract(poolBaseOverviewCTO.liquidityAssetAddr, Erc20Abi, signer)

          const borrowerVaultAddr = poolDynamicOverviewCTO
            ? poolDynamicOverviewCTO.poolAddr
            : poolBaseOverviewCTO.borrowerVaultAddr

          const amountInWei = Erc20TransferAmount.toString() as ERC20Wei

          const b1 = await erc20Contract.balanceOf(borrowerVaultAddr)
          logOT.info('Transfer to borrowerVaultAddr', borrowerVaultAddr, amountInWei)

          await transferLiquidityAsset(
            poolBaseOverviewCTO.liquidityAssetAddr,
            borrowerVaultAddr,
            amountInWei.toString() as ERC20Wei,
            signer as Signer
          )
          logOT.info('Transfer Done', b1.toString(), amountInWei)

          const b2 = await erc20Contract.balanceOf(borrowerVaultAddr)
          dispatch(refreshPoolCTOThunk(poolBaseOverviewCTO.poolAddr))

          logOT.info('Transfer balance before and after', b1.toString(), b2.toString(), amountInWei)
        }

        asyncFunction().catch((e) => {
          logOT.error('Transfer Error', e)
          setActionString(`Error ${e.reason as string}`)
          logOT.error(e)
        })
      }
    }

    useEffect(() => {
      if (poolBaseOverviewCTO) {
        const borrowerWalletAddr = poolBaseOverviewCTO.borrowerWalletAddr
        fetchErc20BalanceForPool(providerReadOnly, borrowerWalletAddr, poolAddr)
          .then((b) => {
            setBorrowerWalletBalance(b)
          })
          .catch((e) => {
            logOT.error(e)
          })
        const borrowerVaultAddr = poolDynamicOverviewCTO
          ? poolDynamicOverviewCTO.poolAddr
          : poolBaseOverviewCTO.borrowerVaultAddr

        fetchErc20BalanceForPool(providerReadOnly, borrowerVaultAddr, poolAddr)
          .then((b) => {
            setBorrowerVaultBalance(b)
          })
          .catch((e) => {
            logOT.error(e)
          })
      }
    }, [poolBaseOverviewCTO])

    const amountNeeded = poolOverviewCTO
      ? Number(poolOverviewCTO?.rolloverNetRepaymentWithFees) - Number(borrowerVaultBalance)
      : Number(poolFlexOverviewCTO?.totalRequestedAssets) - Number(borrowerVaultBalance)

    return (
      <Box
        sx={{
          marginTop: 2,
          bgcolor: 'background.paper',
          border: '2px solid #000',
        }}
      >
        <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
          <Typography
            variant="h4"
            sx={{ marginBottom: 4 }}
            onClick={(_) => {
              navigate(`/pools/${poolBaseOverviewCTO.poolAddr}`)
            }}
          >
            Transfer - {poolBaseOverviewCTO.name}{' '}
          </Typography>
          <Typography variant="body" sx={{ marginBottom: 4 }}>
            To BorrowerWallet Balance {borrowerWalletBalance.toString()}
          </Typography>
          <Typography variant="body" sx={{ marginBottom: 4 }}>
            To BorrowerVault{' '}
            {poolDynamicOverviewCTO ? poolDynamicOverviewCTO.poolAddr : poolBaseOverviewCTO.borrowerVaultAddr}
          </Typography>
          <Typography variant="body" sx={{ marginBottom: 4 }}>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Balance{'  '}
            {erc20WeiToCommaDecString(borrowerVaultBalance)} {poolBaseOverviewCTO.liquidityTokenSymbol}
          </Typography>
          {poolOverviewCTO && (
            <Typography variant="body" sx={{ marginBottom: 4 }}>
              Total Repayment of {erc20WeiToCommaDecString(poolOverviewCTO?.rolloverNetRepaymentWithFees)}{' '}
              {poolBaseOverviewCTO.liquidityTokenSymbol}
            </Typography>
          )}
          {poolFlexOverviewCTO && (
            <Typography variant="body" sx={{ marginBottom: 4 }}>
              Total Repayment of {erc20WeiToCommaDecString(poolOverviewCTO?.totalRequestedAssets)}{' '}
              {poolBaseOverviewCTO.liquidityTokenSymbol}
            </Typography>
          )}
          <Typography variant="body" sx={{ marginBottom: 4 }}>
            Additional Needed {amountNeeded ?? 0 / 1e6} in Wei{' '}
          </Typography>

          {poolOverviewCTO && (
            <Typography variant="body" sx={{ marginBottom: 4 }}>
              {`Total Early Withdraw accepted principal ${
                (earlyWithdrawApprovedPrincipal ?? 0) / 1e6
              } in ${poolBaseOverviewCTO.liquidityTokenSymbol} `}
            </Typography>
          )}

          <TextField
            id="outlined-controlled"
            label={`${poolBaseOverviewCTO.liquidityTokenSymbol} `}
            type={'number'}
            value={Erc20TransferAmount}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setErc20TransferAmount(Number(event.target.value))
            }}
          />
          {poolBaseOverviewCTO && (
            <Button disabled={performingAction} variant="contained" sx={buttonSx} onClick={handleUsdcTransfer}>
              {' '}
              Transfer {Number(Erc20TransferAmount).toLocaleString()} To BorrowerVault
            </Button>
          )}

          {poolDynamicOverviewCTO && (
            <Button
              disabled={performingAction}
              variant="contained"
              sx={{ ...buttonSx, maxWidth: '400px' }}
              onClick={handleUsdcTransferToPoolFromLender}
            >
              Transfer {Number(Erc20TransferAmount).toLocaleString()} To Pool From lender1
            </Button>
          )}
          {poolDynamicOverviewCTO && (
            <Button
              disabled={performingAction}
              variant="contained"
              sx={{ ...buttonSx, maxWidth: '400px' }}
              onClick={handleUsdcTransferToPoolFromBorrower}
            >
              Transfer {Number(Erc20TransferAmount).toLocaleString()} To Pool From Borrower
            </Button>
          )}
        </Box>
      </Box>
    )
  }
  // const [updating, setUpdating] = React.useState<boolean>(false)

  const fetchTransaction = (): void => {
    if (providerReadOnly) {
      providerReadOnly
        .getTransactionReceipt(transactionHash)
        .then((output: TransactionReceipt | null): void => {
          logOT.debug('Transaction output', output)
          if (output) setTransactionOutput(output)
        })
        .catch((e: EthersError) => {
          setActionString(`Error ${e.reason as string}`)
          logOT.error(e)
        })
    }
  }

  const handleAdvanceInDays = (): void => {
    logOT.debug('advance time in days', numberOfDays)
    setPerformingAction(true)
    setActionString(`Advancing time by ${numberOfDays} days`)

    logOT.info('handleAdvanceInDays ', numberOfDays, !!providerJsonRpc)
    if (providerJsonRpc) {
      advanceTimeInHours(providerJsonRpc, numberOfDays * 24)
        .then((date) => {
          logOT.info('handleAdvanceInDays success')
          logOT.debug('advance time success')
          logOT.debug('New date', date)
          webSocket?.send(wsServerActionClockRefresh(chainConfigurationName))
        })
        .catch((e) => {
          logOT.info('handleAdvanceInDays error', e)
          setActionString(`Error ${e.reason as string}`)
          logOT.error(e)
        })
    }
  }

  const handleAdvanceInHours = (): void => {
    logOT.debug('advance time in hours', numberOfHours)
    setPerformingAction(true)
    setActionString(`Advancing time by ${numberOfHours} Hours`)

    logOT.debug('8545')

    if (providerReadOnly) {
      logOT.debug('advance time in hours', numberOfHours)

      advanceTimeInHours(providerJsonRpc, numberOfHours)
        .then((date) => {
          logOT.debug('New date', date)
          webSocket?.send(wsServerActionClockRefresh(chainConfigurationName))
          // dispatch(poolActions.crank(ETH_ADDR_0))
        })
        .catch((e) => {
          logOT.debug('Error Advance time in hours', numberOfHours)

          setActionString(`Error ${e.reason as string}`)
          logOT.error(e)
        })
    }
  }

  const handleRevertSnapshot = (): void => {
    setPerformingAction(true)
    setActionString('Reverting Snapshot')

    if (providerReadOnly) {
      logOT.debug('advance time in hours', numberOfHours)

      const revertLoop = async (): Promise<void> => {
        let endLoop = false
        let snapId = 374

        while (!endLoop && snapId < 5000) {
          const n = `0x${Number(snapId).toString(16)}`
          const success = await providerJsonRpc.send('evm_revert', [n])
          endLoop = success
          if (success) {
            const snapshotId = await providerJsonRpc.send('evm_snapshot', [])
            logOT.info('snapshotId', snapshotId)
            setActionString('Reverting Snapshot Succeed')
          }

          snapId += 1
        }
      }
      revertLoop().catch((e) => {
        setActionString(`Error ${e.reason as string}`)
        logOT.error(e)
      })
    }
  }
  const performingActionEnd = (): void => {
    setPerformingAction(false)
    setActionString('')
  }
  const buttonSx = {
    padding: '11px 16px 11px 16px',
    marginLeft: '10px',
    borderRadius: '6px',
    marginTop: '8px',
    marginBottom: '8px',
    maxWidth: '200px',
  }

  try {
    return (
      <div>
        <Snackbar
          open={performingAction}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={2000}
          onClose={performingActionEnd}
        >
          <Typography>{actionString}</Typography>
        </Snackbar>

        <Box
          sx={{
            marginTop: 2,
            bgcolor: 'background.paper',
            border: '2px solid #000',
          }}
        >
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
            <Typography variant="h4" sx={{ marginBottom: 4 }}>
              Advance time by days or hours
            </Typography>

            <TextField
              id="outlined-controlled"
              label="Number of Days"
              type={'number'}
              value={numberOfDays}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setNumberOfDays(Number(event.target.value))
              }}
            />
            <Button disabled={performingAction} variant="contained" sx={buttonSx} onClick={handleAdvanceInDays}>
              {' '}
              Advance {numberOfDays} days
            </Button>
            <Divider />
            <TextField
              id="outlined-controlled"
              label="Number of Hours"
              type={'number'}
              value={numberOfHours}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setNumberOfHours(Number(event.target.value))
              }}
            />
            <Button disabled={performingAction} variant="contained" sx={buttonSx} onClick={handleAdvanceInHours}>
              {' '}
              Advance {numberOfHours} hours
            </Button>
          </Box>
        </Box>
        {transferBlocks}

        <Box
          sx={{
            marginTop: 2,
            bgcolor: 'background.paper',
            border: '2px solid #000',
          }}
        >
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
            <Typography variant="h4" sx={{ marginBottom: 4 }}>
              Revert Demo To Empty setting{' '}
            </Typography>

            <Button disabled={performingAction} variant="contained" sx={buttonSx} onClick={handleRevertSnapshot}>
              Revert to Beginning
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            marginTop: 2,
            bgcolor: 'background.paper',
            border: '2px solid #000',
          }}
        >
          <AddUsdcForm />
        </Box>
        <Box
          sx={{
            marginTop: 2,
            bgcolor: 'background.paper',
            border: '2px solid #000',
          }}
        >
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom component="div">
                Transaction
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="tx"
                label="TransactionHash"
                variant="standard"
                value={transactionHash}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setTransactionHash(event.target.value)
                }}
              />
              <Button disabled={performingAction} variant="contained" sx={buttonSx} onClick={fetchTransaction}>
                {' '}
                Fetch Transaction
              </Button>
            </Grid>
            <Grid item xs={12}>
              <pre>
                <code>{JSON.stringify(transactionOutput, null, 2)}</code>
              </pre>
            </Grid>
          </Grid>
        </Box>
      </div>
    )
  } catch (e: unknown) {
    return (
      <Typography variant="bodyBold" gutterBottom component="div">
        {' '}
        ERROR {(e as Error).toString()}{' '}
      </Typography>
    )
  }
}
